import clsx from 'clsx';

import { getSDDOTReplenishmentSubtextOptions } from '@atom/components/common/requests/customTenantUtilities';
import { Button, DatePicker, Icon, TextField } from '@atom/mui';
import colors from '@atom/styles/colors';
import { ReplenishmentUpdateInput } from '@atom/types/assetRequest';
import { convertDateToMillisGMTMidday } from '@atom/utilities/timeUtilities';
import { isNilOrEmpty } from '@atom/utilities/validationUtilities';

import classes from './sddotView.module.css';

const DECIMAL_LIMIT = 4;

const styles = {
  label: {
    color: colors.neutral.dim,
  },
  currencyIcon: {
    marginRight: '2px',
    marginBottom: '2px',
    color: colors.neutral.dim,
    fontSize: '16px',
  },
  icon: {
    marginRight: '1rem',
  },
  largeInput: {
    width: '10rem',
  },
  smallInput: {
    width: '6rem',
  },
};

interface Props {
  assetUnit: string;
  totalOrdered: number;
  totalReceived?: number;
  replenishmentInputs: Partial<ReplenishmentUpdateInput>;
  updateReplenishmentInput: (
    value: any,
    property: keyof ReplenishmentUpdateInput,
  ) => void;
  onEdit?: () => void;
  onCancel?: () => void;
  totalRemainingNegativeError: boolean;
  loading: boolean;
}

const EditReplenishmentForm = ({
  assetUnit,
  totalOrdered,
  totalReceived,
  replenishmentInputs,
  updateReplenishmentInput,
  onEdit,
  onCancel,
  totalRemainingNegativeError,
  loading,
}: Props) => {
  const numberUpdate = (
    value: any,
    property: keyof ReplenishmentUpdateInput,
  ) => {
    const newValue = isNilOrEmpty(value) ? null : Math.abs(Number(value));
    updateReplenishmentInput(newValue, property);
  };

  const handleDateChange = (
    newValue: Date | any,
    property: keyof ReplenishmentUpdateInput,
  ) => {
    if (!newValue || newValue.toString() === 'Invalid Date') {
      updateReplenishmentInput(null, property);
    } else {
      const mappedDate = convertDateToMillisGMTMidday(newValue);
      updateReplenishmentInput(mappedDate, property);
    }
  };

  const costUpdate = (value: any, property: keyof ReplenishmentUpdateInput) => {
    const newValue =
      value !== '' ? parseFloat(Math.abs(Number(value)).toFixed(4)) : null;
    updateReplenishmentInput(newValue, property);
  };

  const calculateValue = (property: keyof ReplenishmentUpdateInput) => {
    const { quantityReceived, unitCost, totalCost } = replenishmentInputs;

    const totalCostCalc = parseFloat(
      (Number(quantityReceived) * Number(unitCost)).toFixed(DECIMAL_LIMIT),
    );
    const costCalc =
      quantityReceived === 0
        ? 0
        : parseFloat(
            (Number(totalCost) / Number(quantityReceived)).toFixed(
              DECIMAL_LIMIT,
            ),
          );

    const updatedValue = property === 'totalCost' ? totalCostCalc : costCalc;
    updateReplenishmentInput(updatedValue, property);
  };

  const subtextValue = getSDDOTReplenishmentSubtextOptions(
    totalOrdered,
    totalReceived,
    replenishmentInputs?.quantityReceived,
    assetUnit || '',
  );

  const subtextStyleName = clsx(
    classes.remainingSubtext,
    subtextValue?.icon === 'warning' && classes.warning,
  );

  const iconColor =
    subtextValue?.icon === 'warning' ? colors.brand.orange : colors.brand.green;

  const showUnitCostCalculate =
    !isNilOrEmpty(replenishmentInputs?.quantityReceived) &&
    !isNilOrEmpty(replenishmentInputs?.totalCost);

  const showTotalCostCalculate =
    !isNilOrEmpty(replenishmentInputs?.quantityReceived) &&
    !isNilOrEmpty(replenishmentInputs?.unitCost);

  const saveDisabled =
    isNilOrEmpty(replenishmentInputs?.invoiceNumber) ||
    isNilOrEmpty(replenishmentInputs?.dateReceived) ||
    isNilOrEmpty(replenishmentInputs?.quantityReceived) ||
    isNilOrEmpty(replenishmentInputs?.unitCost) ||
    isNilOrEmpty(replenishmentInputs?.totalCost) ||
    loading ||
    totalRemainingNegativeError;

  const dateReceived = replenishmentInputs?.dateReceived
    ? new Date(replenishmentInputs?.dateReceived)
    : null;

  return (
    <div className={classes.formContainer}>
      <div className={classes.formTitle}>Replenishment</div>
      <div className={classes.formSubtext}>
        The unit cost of this inventory item will be re-calculated by taking
        into account the quantity received and the unit cost of this
        replenishment.
      </div>
      <div className={classes.formInputRow}>
        <TextField
          slotProps={{ inputLabel: { style: styles.label } }}
          style={styles.largeInput}
          value={replenishmentInputs?.invoiceNumber}
          label="Invoice Number"
          onChange={event =>
            updateReplenishmentInput(event.target.value, 'invoiceNumber')
          }
          data-cy="assetRequestEditReplenishmentFormInvoiceNumber"
        />
        <DatePicker
          InputLabelProps={{ sx: styles.label }}
          style={styles.largeInput}
          label="Date Received"
          value={dateReceived}
          onChange={dateValue => handleDateChange(dateValue, 'dateReceived')}
          data-cy="assetRequestEditReplenishmentFormDateReceived"
        />
        <TextField
          slotProps={{ inputLabel: { style: styles.label } }}
          style={styles.largeInput}
          type="number"
          value={replenishmentInputs?.quantityReceived}
          label={`Total Quantity Received (${assetUnit || ''})`}
          onChange={event =>
            numberUpdate(event.target.value, 'quantityReceived')
          }
          data-cy="assetRequestEditReplenishmentFormQuantityReceived"
          helperText={
            totalRemainingNegativeError &&
            'Quantity will cause Total Remaining to be negative'
          }
          error={totalRemainingNegativeError}
        />
        <TextField
          slotProps={{
            inputLabel: { style: styles.label },
            input: {
              startAdornment: (
                <Icon style={styles.currencyIcon}>attach_money</Icon>
              ),
            },
          }}
          style={styles.smallInput}
          type="number"
          value={replenishmentInputs?.unitCost}
          label="Unit Cost"
          onChange={event => costUpdate(event.target.value, 'unitCost')}
          data-cy="assetRequestEditReplenishmentFormUnitCost"
          helperText={
            showUnitCostCalculate && (
              <div
                className={classes.calculateButton}
                onClick={() => calculateValue('unitCost')}
                data-cy="assetRequestEditReplenishmentFormUnitCostCalculate"
              >
                Calculate
              </div>
            )
          }
        />
        <TextField
          slotProps={{
            inputLabel: { style: styles.label },
            input: {
              startAdornment: (
                <Icon style={styles.currencyIcon}>attach_money</Icon>
              ),
            },
          }}
          style={styles.smallInput}
          type="number"
          value={replenishmentInputs?.totalCost}
          label="Total Cost"
          onChange={event => costUpdate(event.target.value, 'totalCost')}
          data-cy="assetRequestEditReplenishmentFormTotalCost"
          helperText={
            showTotalCostCalculate && (
              <div
                className={classes.calculateButton}
                onClick={() => calculateValue('totalCost')}
                data-cy="assetRequestEditReplenishmentFormTotalCostCalculate"
              >
                Calculate
              </div>
            )
          }
        />
      </div>
      <div className={classes.inputContainer}>
        <TextField
          slotProps={{ inputLabel: { style: styles.label } }}
          fullWidth
          value={replenishmentInputs?.comments}
          label="Comments (optional)"
          onChange={event =>
            updateReplenishmentInput(event.target.value, 'comments')
          }
          data-cy="assetRequestEditReplenishmentFormComment"
        />
      </div>
      {subtextValue && (
        <div className={subtextStyleName}>
          <Icon color={iconColor} style={styles.icon}>
            {subtextValue.icon}
          </Icon>
          <div>{subtextValue.message}</div>
        </div>
      )}
      {onEdit && onCancel && (
        <div className={classes.createButtons}>
          <Button
            onClick={onCancel}
            style={{ marginRight: '0.5rem' }}
            data-cy="assetRequestEditReplenishmentCancel"
          >
            Cancel
          </Button>
          <Button
            color="primary"
            variant="contained"
            onClick={onEdit}
            disabled={saveDisabled}
            data-cy="assetRequestEditReplenishmentCreate"
          >
            Save
          </Button>
        </div>
      )}
    </div>
  );
};

export default EditReplenishmentForm;
